import { useEffect } from "react";

/**
 * Scroll reveal (defensive):
 *  - Default: .reveal elements are fully visible.
 *  - On mount we tag ONLY elements whose top is below the initial viewport
 *    with `.will-animate` (which adds opacity:0 + translateY). Elements already
 *    visible on load keep their default visible state — no flash, no stuck-hidden.
 *  - IntersectionObserver then adds `.is-visible` when they enter the viewport.
 */
export default function useScrollReveal(selector = ".reveal") {
  useEffect(() => {
    const items = Array.from(document.querySelectorAll(selector));
    if (!items.length) return;

    const viewportH = window.innerHeight;
    const pending = [];
    items.forEach((el) => {
      const rect = el.getBoundingClientRect();
      // Only opt-in to the hidden→visible animation if the element is
      // meaningfully below the fold at load time.
      if (rect.top >= viewportH - 80) {
        el.classList.add("will-animate");
        pending.push(el);
      }
    });

    if (!pending.length) return;

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );

    pending.forEach((el) => obs.observe(el));

    // Safety net: after 1.5s, force-reveal anything still pending
    // (covers edge cases where observer misses or page is hidden).
    const safety = setTimeout(() => {
      pending.forEach((el) => el.classList.add("is-visible"));
    }, 1500);

    return () => {
      obs.disconnect();
      clearTimeout(safety);
    };
  }, [selector]);
}
